import React from 'react';
import PropTypes from 'prop-types';
import { Plus, Minus } from 'lucide-react';
import { newCustomSpec } from '../../utils/customSpecs';
import './CustomSpecsEditor.css';

/**
 * Label/details rows for the specs that don't fit the fixed spec fields.
 * Rows are edited in place; trimming and dropping label-less rows happens on
 * save so typing isn't fought mid-keystroke.
 */
export default function CustomSpecsEditor({
  specs,
  onChange,
  readOnly = false,
  title = 'Custom Specs',
  inputClassName = '',
  compact = false,
}) {
  const rows = Array.isArray(specs) ? specs : [];

  // Host forms style their own inputs (.item-grid input, .if-input); only fall
  // back to our own look when the caller hasn't given us one.
  const inputClass = `cs-editor__input ${inputClassName || 'cs-editor__input--default'}`;

  const updateRow = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  const addRow = () => onChange([...rows, newCustomSpec()]);

  if (readOnly && rows.length === 0) return null;

  return (
    <div
      className={`cs-editor${compact ? ' cs-editor--compact' : ''}${
        readOnly ? ' cs-editor--readonly' : ''
      }`}
    >
      {title && <div className="cs-editor__title">{title}</div>}

      {rows.length > 0 && (
        <div className="cs-editor__head-row">
          <span className="cs-editor__head-label">Label</span>
          <span className="cs-editor__head-label">Details</span>
        </div>
      )}

      {rows.map((row, index) => (
        <div className="cs-editor__row" key={row.id || `spec-${index}`}>
          <input
            className={inputClass}
            type="text"
            placeholder="e.g. COM Yardage"
            value={row.label || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => updateRow(index, { label: e.target.value })}
          />
          <input
            className={inputClass}
            type="text"
            placeholder="e.g. 29.5 Yards"
            value={row.value || ''}
            readOnly={readOnly}
            onChange={readOnly ? undefined : (e) => updateRow(index, { value: e.target.value })}
          />
          {!readOnly && (
            <button
              type="button"
              className="cs-editor__remove"
              onClick={() => removeRow(index)}
              title="Remove spec"
            >
              <Minus size={13} />
            </button>
          )}
        </div>
      ))}

      {!readOnly && (
        <button type="button" className="cs-editor__add" onClick={addRow}>
          <Plus size={13} /> Add spec
        </button>
      )}
    </div>
  );
}

CustomSpecsEditor.propTypes = {
  specs: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  title: PropTypes.string,
  inputClassName: PropTypes.string,
  compact: PropTypes.bool,
};
